/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { AdminInvestmentDocument } from './AdminInvestmentDocument';
import {
    AdminInvestmentDocumentFromJSON,
    AdminInvestmentDocumentFromJSONTyped,
    AdminInvestmentDocumentToJSON,
} from './AdminInvestmentDocument';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
} from './Document';
import type { AdminCostsVariables } from './AdminCostsVariables';
import {
    AdminCostsVariablesFromJSON,
    AdminCostsVariablesFromJSONTyped,
    AdminCostsVariablesToJSON,
} from './AdminCostsVariables';

/**
 * 
 * @export
 * @interface AdminDistributorProductDistributionInformation
 */
export interface AdminDistributorProductDistributionInformation {
    /**
     * 
     * @type {string}
     * @memberof AdminDistributorProductDistributionInformation
     */
    readonly id: string;
    /**
     * Product name registered with Effecta for Compliance process
     * @type {string}
     * @memberof AdminDistributorProductDistributionInformation
     */
    complianceProductName: string;
    /**
     * Defines whether self disclosure is required for investments. Will be ignored for ECSP products.
     * @type {boolean}
     * @memberof AdminDistributorProductDistributionInformation
     */
    selfDisclosureRequired: boolean;
    /**
     * unit steps an investor can choose from in commitment step as comma separated string. Note: if use_units is False, the investment amount will be shown so choose units accordingly taking the price per unit into account
     * @type {string}
     * @memberof AdminDistributorProductDistributionInformation
     */
    unitSteps?: string;
    /**
     * Does the investor have to enter units in the commitment step? If False investors need to enter the amount of the investment
     * @type {boolean}
     * @memberof AdminDistributorProductDistributionInformation
     */
    useUnits: boolean;
    /**
     * 
     * @type {Date}
     * @memberof AdminDistributorProductDistributionInformation
     */
    readonly createdAt: Date;
    /**
     * 
     * @type {Date}
     * @memberof AdminDistributorProductDistributionInformation
     */
    readonly updatedAt: Date;
    /**
     * 
     * @type {number}
     * @memberof AdminDistributorProductDistributionInformation
     */
    investmentExperienceMinScore: number;
    /**
     * 
     * @type {number}
     * @memberof AdminDistributorProductDistributionInformation
     */
    serviceExperienceMinScore: number;
    /**
     * 
     * @type {Document}
     * @memberof AdminDistributorProductDistributionInformation
     */
    readonly productImage: Document;
    /**
     * 
     * @type {AdminCostsVariables}
     * @memberof AdminDistributorProductDistributionInformation
     */
    costsvariables: AdminCostsVariables;
    /**
     * 
     * @type {Array<AdminInvestmentDocument>}
     * @memberof AdminDistributorProductDistributionInformation
     */
    readonly investmentDocuments: Array<AdminInvestmentDocument>;
}

/**
 * Check if a given object implements the AdminDistributorProductDistributionInformation interface.
 */
export function instanceOfAdminDistributorProductDistributionInformation(value: object): value is AdminDistributorProductDistributionInformation {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('complianceProductName' in value) || value['complianceProductName'] === undefined) return false;
    if (!('selfDisclosureRequired' in value) || value['selfDisclosureRequired'] === undefined) return false;
    if (!('useUnits' in value) || value['useUnits'] === undefined) return false;
    if (!('createdAt' in value) || value['createdAt'] === undefined) return false;
    if (!('updatedAt' in value) || value['updatedAt'] === undefined) return false;
    if (!('investmentExperienceMinScore' in value) || value['investmentExperienceMinScore'] === undefined) return false;
    if (!('serviceExperienceMinScore' in value) || value['serviceExperienceMinScore'] === undefined) return false;
    if (!('productImage' in value) || value['productImage'] === undefined) return false;
    if (!('costsvariables' in value) || value['costsvariables'] === undefined) return false;
    if (!('investmentDocuments' in value) || value['investmentDocuments'] === undefined) return false;
    return true;
}

export function AdminDistributorProductDistributionInformationFromJSON(json: any): AdminDistributorProductDistributionInformation {
    return AdminDistributorProductDistributionInformationFromJSONTyped(json, false);
}

export function AdminDistributorProductDistributionInformationFromJSONTyped(json: any, ignoreDiscriminator: boolean): AdminDistributorProductDistributionInformation {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'complianceProductName': json['compliance_product_name'],
        'selfDisclosureRequired': json['self_disclosure_required'],
        'unitSteps': json['unit_steps'] == null ? undefined : json['unit_steps'],
        'useUnits': json['use_units'],
        'createdAt': (new Date(json['created_at'])),
        'updatedAt': (new Date(json['updated_at'])),
        'investmentExperienceMinScore': json['investment_experience_min_score'],
        'serviceExperienceMinScore': json['service_experience_min_score'],
        'productImage': DocumentFromJSON(json['product_image']),
        'costsvariables': AdminCostsVariablesFromJSON(json['costsvariables']),
        'investmentDocuments': ((json['investment_documents'] as Array<any>).map(AdminInvestmentDocumentFromJSON)),
    };
}

export function AdminDistributorProductDistributionInformationToJSON(value?: Omit<AdminDistributorProductDistributionInformation, 'id'|'created_at'|'updated_at'|'product_image'|'investment_documents'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'compliance_product_name': value['complianceProductName'],
        'self_disclosure_required': value['selfDisclosureRequired'],
        'unit_steps': value['unitSteps'],
        'use_units': value['useUnits'],
        'investment_experience_min_score': value['investmentExperienceMinScore'],
        'service_experience_min_score': value['serviceExperienceMinScore'],
        'costsvariables': AdminCostsVariablesToJSON(value['costsvariables']),
    };
}

